import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Settings, Save, Cpu, RefreshCw, Palette, Check } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import NotificationSettings from "@/components/NotificationSettings";
import AvatarSettings from "@/components/AvatarSettings";
import BotTokensSettings from "@/components/BotTokensSettings";
import BrandingSettings from "@/components/BrandingSettings";
import AtenaSettings from "@/components/AtenaSettings";
import AiProviderKeys from "@/components/AiProviderKeys";
import StatusSettings from "@/components/StatusSettings";
import DigestSettings from "@/components/DigestSettings";
import BriefingSettings from "@/components/BriefingSettings";
import KMeetSettings from "@/components/KMeetSettings";
import CalendarSettings from "@/components/CalendarSettings";
import TicketProjectsSettings from "@/components/TicketProjectsSettings";
import RoutingSettings from "@/components/RoutingSettings";
import SeifSettings from "@/components/SeifSettings";
import RutineSettings from "@/components/RutineSettings";
import InvatareSettings from "@/components/InvatareSettings";
import TablouDeBord from "@/components/TablouDeBord";

export default function SettingsView({ currentUser, workspaceId, isOwner }) {
  const { theme, setTheme, themes } = useTheme();
  const [endpoint, setEndpoint] = useState("");
  const [model, setModel] = useState("custom-model");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Model auto-list
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsTried, setModelsTried] = useState(false);
  const [manualModel, setManualModel] = useState(false);

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const r = await api.get("/ai/models");
      const list = r.data.models || [];
      setModels(list);
      setManualModel(list.length === 0); // no list (custom/unreachable) -> free text
    } catch {
      setModels([]);
      setManualModel(true);
    } finally {
      setModelsTried(true);
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    api.get("/ai/config").then((r) => {
      setEndpoint(r.data.endpoint || "");
      setModel(r.data.model || "custom-model");
    }).finally(() => {
      setLoaded(true);
      fetchModels();
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai/config", { endpoint, model });
      toast.success("Setările au fost salvate");
      fetchModels(); // endpoint may have changed -> refresh the list
    } catch {
      toast.error("Salvarea nu a reușit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-14 px-6 flex items-center gap-2 border-b border-line/10 bg-bg">
        <Settings className="w-4 h-4 text-fg/50" />
        <h2 className="font-bold tracking-tight">Setări</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-6 max-w-3xl">
        <div className="mb-6"><AvatarSettings currentUser={currentUser} /></div>
        <TicketProjectsSettings workspaceId={workspaceId} isOwner={isOwner} />
        {isOwner && <StatusSettings workspaceId={workspaceId} />}
        {isOwner && <KMeetSettings workspaceId={workspaceId} />}
        {isOwner && <DigestSettings workspaceId={workspaceId} />}
        {isOwner && <BriefingSettings workspaceId={workspaceId} />}
        {isOwner && <AtenaSettings workspaceId={workspaceId} />}
        <TablouDeBord workspaceId={workspaceId} />
        <InvatareSettings workspaceId={workspaceId} />
        <RutineSettings workspaceId={workspaceId} />
        {isOwner && <SeifSettings workspaceId={workspaceId} />}
        {isOwner && <RoutingSettings workspaceId={workspaceId} />}
        {isOwner && <BrandingSettings workspaceId={workspaceId} />}
        {/* Bot tokens mint workspace credentials — owner only, admins do not see them. */}
        {isOwner && <BotTokensSettings />}
        <section className="bg-surface border border-line/10 mt-6">
          <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-brand" />
            <h3 className="font-bold tracking-tight">AI intern</h3>
            <span className="label-tiny ml-2">// OLLAMA · VLLM · LM STUDIO</span>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-xs text-fg/50 leading-relaxed">
              Îndreaptă Qedara Chat spre orice endpoint compatibil OpenAI <code className="mono text-brand">/v1/chat/completions</code> endpoint (Ollama, vLLM, LM Studio sau propriul tău server de modele). Ollama nu cere cheie; cheile pentru Claude și GPT se configurează în secțiunile lor de mai jos.
            </p>
            {!loaded ? (
              <div className="py-8 text-center label-tiny text-brand">se încarcă configurarea<span className="cursor-blink">_</span></div>
            ) : (
              <>
            <div>
              <label className="label-tiny block mb-2">URL endpoint</label>
              <input
                data-testid="ai-endpoint-input"
                className="input-flat mono"
                placeholder="http://qedara-ollama:11434/v1/chat/completions"
                value={endpoint} onChange={(e) => setEndpoint(e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-tiny">Model</label>
                <button
                  type="button"
                  data-testid="ai-models-refresh"
                  onClick={fetchModels} disabled={loadingModels}
                  className="flex items-center gap-1 text-[11px] text-brand-2 hover:text-brand disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingModels ? "animate-spin" : ""}`} /> reîmprospătează
                </button>
              </div>
              {!manualModel && models.length > 0 ? (
                <select
                  data-testid="ai-model-select"
                  className="input-flat mono"
                  value={model} onChange={(e) => setModel(e.target.value)}
                >
                  {model && !models.includes(model) && <option value={model}>{model} (propriu)</option>}
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : (
                <input
                  data-testid="ai-model-input"
                  className="input-flat mono"
                  placeholder="custom-model"
                  value={model} onChange={(e) => setModel(e.target.value)}
                />
              )}
              <div className="mt-1 text-[11px] text-fg/40">
                {models.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setManualModel((v) => !v)}
                    className="text-brand-2 hover:text-brand"
                  >
                    {manualModel ? "← alege din listă" : "enter manually instead"}
                  </button>
                ) : (
                  <span>
                    {modelsTried
                      ? "Endpointul nu listează modele — scrie numele modelului (vLLM / LM Studio)."
                      : "Salvează endpointul, apoi reîmprospătează pentru a lista modelele."}
                  </span>
                )}
              </div>
            </div>
            <button
              data-testid="save-ai-config-button"
              onClick={save} disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? "Se salvează…" : "Salvează setările"}
            </button>
              </>
            )}
          </div>
        </section>
        {isOwner && <AiProviderKeys workspaceId={workspaceId} />}

        <NotificationSettings />
        <CalendarSettings workspaceId={workspaceId} />

        <section className="bg-surface border border-line/10 mt-6">
          <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
            <Palette className="w-4 h-4 text-brand" />
            <h3 className="font-bold tracking-tight">Aspect</h3>
            <span className="label-tiny ml-2">// TEMĂ</span>
          </div>
          <div className="p-5">
            <p className="text-xs text-fg/50 leading-relaxed mb-4">
              Alege cum arată Qedara Chat. Alegerea se salvează în cont și te urmează pe orice dispozitiv.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="theme-grid">
              {themes.map((t) => {
                const active = theme === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`theme-option-${t.id}`}
                    onClick={() => setTheme(t.id)}
                    className={`relative flex items-center gap-3 px-3 py-2.5 border text-left transition-colors ${
                      active ? "border-brand bg-brand/10" : "border-line/10 hover:border-line/30"
                    }`}
                  >
                    <span className="flex -space-x-1.5 flex-shrink-0">
                      {t.swatch.map((c, i) => (
                        <span key={i} className="w-4 h-4 rounded-full border border-line/20" style={{ background: c }} />
                      ))}
                    </span>
                    <span className="text-sm font-medium flex-1">{t.label}</span>
                    {active && <Check className="w-4 h-4 text-brand flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-surface border border-line/10 mt-6">
          <div className="px-5 py-4 border-b border-line/10">
            <h3 className="font-bold tracking-tight">Cont</h3>
          </div>
          <div className="p-5 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="label-tiny w-24">Nume</span>
              <span>{currentUser.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="label-tiny w-24">Email</span>
              <span className="mono text-fg/70">{currentUser.email}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
